class CatpipReservationTimer extends HTMLElement {
  connectedCallback() {
    this.timeElement = this.querySelector('[data-catpip-reservation-time]');
    this.labelElement = this.querySelector('[data-catpip-reservation-label]');
    this.mode = this.dataset.mode || 'off';
    this.durationMinutes = Math.max(1, Number(this.dataset.durationMinutes || 10));
    this.cartToken = this.dataset.cartToken || '';
    this.activeLabel = this.dataset.activeLabel || this.labelElement?.textContent.trim() || '';
    this.storageKey = `catpipReservationPreview:${this.cartToken || 'cart'}`;
    this.handleReservationUpdate = this.handleReservationUpdate.bind(this);
    document.addEventListener('catpip:reservation-update', this.handleReservationUpdate);

    if (this.mode === 'backend') {
      const expiresAt = this.parseExpiry(this.dataset.expiresAt);
      if (expiresAt > Date.now()) this.setExpiry(expiresAt);
    }

    requestAnimationFrame(() => {
      if (this.closest('cart-drawer')?.classList.contains('active')) this.onDrawerOpen();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('catpip:reservation-update', this.handleReservationUpdate);
    this.stop();
  }

  parseExpiry(value) {
    if (value === undefined || value === null || value === '') return 0;
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) return numericValue < 1e12 ? numericValue * 1000 : numericValue;
    const parsedValue = Date.parse(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  previewExpiry() {
    let expiresAt = 0;
    try {
      expiresAt = Number(sessionStorage.getItem(this.storageKey) || 0);
    } catch (error) {
      expiresAt = 0;
    }

    if (expiresAt <= Date.now()) {
      expiresAt = Date.now() + this.durationMinutes * 60 * 1000;
      try {
        sessionStorage.setItem(this.storageKey, String(expiresAt));
      } catch (error) {
        // The visual preview still works when session storage is unavailable.
      }
    }
    return expiresAt;
  }

  onDrawerOpen() {
    if (this.mode === 'preview') {
      this.setExpiry(this.previewExpiry());
      return;
    }

    if (this.mode !== 'backend') return;
    const expiresAt = this.parseExpiry(this.dataset.expiresAt);
    if (expiresAt > Date.now()) {
      this.setExpiry(expiresAt);
      return;
    }
    this.requestReservation();
  }

  requestReservation() {
    if (this.requested) return;
    this.requested = true;
    this.hidden = true;
    this.dispatchEvent(
      new CustomEvent('catpip:reservation-request', {
        bubbles: true,
        composed: true,
        detail: {
          cartToken: this.cartToken,
          durationMinutes: this.durationMinutes,
          setExpiry: (expiresAt) => this.setExpiry(expiresAt),
        },
      })
    );
  }

  handleReservationUpdate(event) {
    const { cartToken, expiresAt } = event.detail || {};
    if (cartToken && this.cartToken && cartToken !== this.cartToken) return;
    this.setExpiry(expiresAt);
  }

  setExpiry(value) {
    const expiresAt = this.parseExpiry(value);
    if (!expiresAt) return false;
    this.expiresAt = expiresAt;
    this.dataset.expiresAt = new Date(expiresAt).toISOString();
    this.hidden = false;
    this.requested = false;
    this.removeAttribute('data-expired');
    if (this.labelElement) this.labelElement.textContent = this.activeLabel;
    this.stop();
    this.tick();
    if (expiresAt > Date.now()) this.interval = window.setInterval(() => this.tick(), 1000);
    return true;
  }

  tick() {
    if (!this.timeElement || !this.expiresAt) return;
    const remainingSeconds = Math.max(0, Math.ceil((this.expiresAt - Date.now()) / 1000));
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    this.timeElement.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    if (remainingSeconds > 0) return;

    this.stop();
    this.setAttribute('data-expired', '');
    if (this.labelElement && this.dataset.expiredLabel) {
      this.labelElement.textContent = this.dataset.expiredLabel;
    }
  }

  stop() {
    if (!this.interval) return;
    window.clearInterval(this.interval);
    this.interval = null;
  }
}

if (!customElements.get('catpip-reservation-timer')) {
  customElements.define('catpip-reservation-timer', CatpipReservationTimer);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('catpip-cart-drawer-open');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('catpip-reservation-timer')?.onDrawerOpen();
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.documentElement.classList.remove('catpip-cart-drawer-open');
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
